import { Box, TextField, Typography } from '../ui';

interface RichHtmlSourceEditorProps {
  value: string;
  onChange: (html: string) => void;
  sx?: any;
}

/**
 * Heuristic check for "rich" HTML that Tiptap's schema would silently strip
 * (custom <div>/<form>/<input>/<table> structures, embedded <style> blocks).
 *
 * Used by EditLesson to decide whether to render the WYSIWYG TiptapEditor or
 * the raw HTML source editor below. Imported lessons (Thought Industries, etc.)
 * always trip this. Plain Tiptap-authored content does not.
 */
export function isRichHtml(html: string | undefined | null): boolean {
  if (!html) return false;
  return /<(style|div|form|input|table|iframe|section|article)\b/i.test(html);
}

/**
 * Plain HTML source editor for lessons whose content can't survive a Tiptap
 * round-trip. Admins see (and can edit) the raw HTML; the saved string is
 * passed straight through to the database with no parsing.
 *
 * The student-facing viewer (RichHtmlViewer) renders this HTML inside a shadow
 * DOM, so embedded <style> blocks and custom classes work as authored.
 */
export default function RichHtmlSourceEditor({ value, onChange, sx }: RichHtmlSourceEditorProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, width: '100%', ...sx }}>
      <Box
        sx={{
          mb: 1,
          p: 1.5,
          border: '1px solid',
          borderColor: 'warning.light',
          borderRadius: 1,
          bgcolor: 'warning.50',
        }}
      >
        <Typography variant="caption" color="warning.dark">
          ⚠ This lesson contains rich HTML (custom layout, styles, or form elements) that the
          rich-text editor can't render. You're editing the raw HTML source — changes are saved
          verbatim. Use the lesson view to preview.
        </Typography>
      </Box>
      <TextField
        multiline
        fullWidth
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="<html source>"
        sx={{
          flex: 1,
          '& .MuiInputBase-root': {
            height: '100%',
            alignItems: 'flex-start',
            fontFamily: 'monospace',
            fontSize: '13px',
          },
          '& textarea': {
            height: '100% !important',
          },
        }}
      />
    </Box>
  );
}
